import type { BundleUploadRow, UploadTarget } from "../store/useBundleUploads";

export type CaseRowGroup = {
  caseNumber: string;
  /** The case page's label, if any of its rows (or its page) knows it. */
  caseLabel?: string;
  /** The case page on screen — listed first. */
  isActive: boolean;
  /**
   * Section expanded: the user's choice, else the case on screen — or every
   * case while no case page is on screen, so nothing needing a click hides.
   */
  isOpen: boolean;
  rows: BundleUploadRow[];
};

/**
 * MDRS-550: one pop-up holds every case's uploads, so rows are grouped by
 * case — in the order each case first appears, with the case on screen first.
 */
export const groupRowsByCase = (
  rows: BundleUploadRow[],
  activeTarget: UploadTarget | null,
  caseOpen: Record<string, boolean> = {}
): CaseRowGroup[] => {
  const groups = new Map<string, CaseRowGroup>();
  rows.forEach((row) => {
    const isActive = row.caseNumber === activeTarget?.caseNumber;
    const group = groups.get(row.caseNumber) ?? {
      caseNumber: row.caseNumber,
      isActive,
      isOpen: caseOpen[row.caseNumber] ?? (isActive || !activeTarget),
      rows: [],
    };
    group.caseLabel = group.caseLabel ?? row.caseLabel;
    group.rows.push(row);
    groups.set(row.caseNumber, group);
  });

  return [...groups.values()]
    .map((group) =>
      group.isActive && !group.caseLabel
        ? { ...group, caseLabel: activeTarget?.caseLabel }
        : group
    )
    .sort((a, b) => Number(b.isActive) - Number(a.isActive));
};
